import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Language } from '../../common/CountryMarketConfig';
import { CountryMarketDisplay } from '../../components/CountryMarketDisplay';
import { Heading } from '../../components/Heading';
import { Icon, IconName } from '../../components/Icon';
import { Paragraph } from '../../components/Paragraph';
import { Tooltip } from '../../components/Tooltip';
import { useCountryMarketState } from '../../controllers/CountryMarketController/CountryMarketState';
import { useTaxonomyState } from '../../controllers/TaxonomyController/TaxonomyState';
import { GTMBaseContentViewObject, PageTracker } from '../../hooks/GTMUtils';
import { useAppRoutes } from '../../hooks/useAppRoutes';
import { useAuth } from '../../hooks/useAuth';
import { useGTM } from '../../hooks/useGTM';
import { ReactComponent as InnovateMobile } from '../../images/innovate-mobile.svg';
import { ReactComponent as Innovate } from '../../images/innovate.svg';
import { useWindowSize } from '../../shared/useWindowSize';
import { Branch, StepsByBranch, TrackingSchema } from '../../types';
import { getStepsByBranchByMarket } from '../Briefing';
import { useBriefingState } from '../Briefing/BriefingState';
import { useIngredientsState } from '../Briefing/IngredientsState';
import CountryMarketSelector from '../CountryMarketSelector/CountryMarketSelector';
import { CustomerUserSelection } from '../CustomerUserSelection';

export const BriefingSelection: FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [, { setBranchWithSteps, resetAll }] = useBriefingState();
  const [taxonomyState] = useTaxonomyState();
  const [{ countryMarketConfig }] = useCountryMarketState();
  const [ingredientsState] = useIngredientsState();
  const { briefingRoute } = useAppRoutes();
  const dispatch = useDispatch();
  const { updateDataLayer } = useGTM();
  const [{ customer, user, customerUser }] = useAuth();
  const [showCountryMarketSelector, setShowCountryMarketSelector] =
    useState(false);
  const [showCountryMarketDisplay, setShowCountryMarketDisplay] =
    useState(false);

  useEffect(() => {
    // Reset all the briefing data, since we are finished with it
    // (after sending the briefing + loading the recommendations)
    dispatch(resetAll());
  }, [resetAll, dispatch]);

  useEffect(() => {
    /**
     * If a user is an Azelis employee, then hide the country market display
     */
    if (user?.azelis_employee) {
      setShowCountryMarketDisplay(false);
    } else if (customerUser) {
      setShowCountryMarketDisplay(true);
    } else if (user) {
      setShowCountryMarketDisplay(
        Object.keys(user.countries_and_markets || {}).length > 1 ||
          user.customers.length > 1,
      );
    }
  }, [user, customerUser]);

  const selectBranch = useCallback(
    (branch: keyof StepsByBranch) => () => {
      const steps = getStepsByBranchByMarket(
        countryMarketConfig?.eLabMarketSlug,
      )[branch];

      dispatch(setBranchWithSteps({ branch, steps }));

      updateDataLayer({
        event: 'Process Initiated',
        schema: TrackingSchema.process,
        data: {
          stage: 'initiated',
          type: branch,
        },
      });

      navigate(`${briefingRoute}/${branch}/${steps[0]}`);
    },
    [
      briefingRoute,
      countryMarketConfig,
      dispatch,
      navigate,
      setBranchWithSteps,
      updateDataLayer,
    ],
  );

  const [windowWidth] = useWindowSize();

  const inspirationLink = useMemo(() => {
    return `${process.env.REACT_APP_CP_BASEURL}/${i18n.language}/${countryMarketConfig?.customerPortalMarketSegmentId}/formulations/`;
  }, [countryMarketConfig, i18n.language]);

  const areIngredientsPresent = useMemo(
    () =>
      !!(
        countryMarketConfig &&
        ingredientsState[countryMarketConfig.customerPortalMarketSegmentId]
          ?.ingredients?.length
      ),
    [countryMarketConfig, ingredientsState],
  );

  const areTaxonomiesPresent = useMemo(
    () =>
      !!(
        countryMarketConfig &&
        Object.keys(
          taxonomyState[countryMarketConfig.taxonomyMarketAbbreviaton]?.[
            i18n.language as Language
          ]?.taxonomies || {},
        ).length
      ),
    [countryMarketConfig, i18n.language, taxonomyState],
  );

  const briefingBranches = useMemo(() => {
    return [
      {
        name: 'create',
        iconName: 'FineChemicals',
        isAvailable: areTaxonomiesPresent,
      },
      {
        name: 'enhance',
        iconName: 'EnhanceFormulation',
        isAvailable: areTaxonomiesPresent && areIngredientsPresent,
      },
      {
        name: 'fix',
        iconName: 'FixFormulation',
        noBottomBorder: true,
        isAvailable: areTaxonomiesPresent && areIngredientsPresent,
      },
    ].map(({ name, iconName, noBottomBorder, isAvailable }, index) => {
      const modifyOrEnhance =
        countryMarketConfig?.eLabMarketSlug === 'HomeCareIndustrialCleaning'
          ? 'modify'
          : 'enhance';

      const modifyOrEnhanceBranch = name === 'enhance' ? modifyOrEnhance : name;

      return (
        <div className="w-full md:w-4/12 flex-shrink-0" key={index}>
          <div
            className={`md:px-4 ${
              !isAvailable ? 'opacity-50 cursor-disabled' : ''
            }`}
            data-tip={isAvailable || t('components:briefingSelection:loading')}
          >
            <button
              className={`border-gray-400 border flex flex-col items-center justify-center p-4 space-y-5 ${
                noBottomBorder ? '' : ''
              } rounded w-full group ${
                !isAvailable ? 'pointer-events-none' : ''
              }`}
              onClick={selectBranch(modifyOrEnhanceBranch as Branch)}
            >
              <div className="w-1/2 bg-white -mt-20 sm:-mt-16 lg:-mt-20">
                <span className="block w-full h-32 sm:h-24 lg:h-32">
                  <Icon name={iconName as IconName} />
                </span>
              </div>
              <div data-test="page-header">
                <Heading
                  text={t(
                    `components:briefingSelection:buttons:${modifyOrEnhanceBranch}:title`,
                  )}
                  level="h3"
                  align="center"
                />
                <Paragraph marginBottom={5} mdMarginBottom={6}>
                  {t(
                    `components:briefingSelection:buttons:${modifyOrEnhanceBranch}:copy`,
                  )}
                </Paragraph>
              </div>
              <div className="w-10 h-10 p-2 text-white bg-blue-200 rounded-full group-hover:bg-blue-100">
                <Icon name="ArrowRight" />
              </div>
            </button>
          </div>
          {!isAvailable && <Tooltip delayShow={1000} />}
        </div>
      );
    });
  }, [
    areIngredientsPresent,
    areTaxonomiesPresent,
    selectBranch,
    t,
    countryMarketConfig,
  ]);

  return (
    <PageTracker
      gtmTrackingData={{
        ...GTMBaseContentViewObject,
        data: {
          category: 'Home',
          type: 'Overview',
          content: 'Start Innovation Page',
        },
      }}
    >
      {showCountryMarketDisplay && countryMarketConfig && (
        <div
          data-testid="country-market-display"
          onClick={() => {
            setShowCountryMarketSelector(true);
          }}
        >
          <CountryMarketDisplay
            countries={[countryMarketConfig.countryCluster]}
            markets={[countryMarketConfig.marketDisplayName]}
            customerName={customer?.customer_name ?? ''}
          />
        </div>
      )}

      <CustomerUserSelection />

      <div className="flex flex-col flex-1 items-center px-5 lg:px-0">
        <div className="flex flex-wrap justify-center xl:max-w-6xl space-y-16 pt-32">
          <div className="w-full max-w-[390px] md:max-w-[680px] lg:max-w-[930px]">
            <div className="text-gray-700 fill-current">
              {windowWidth >= 768 ? <Innovate /> : <InnovateMobile />}
            </div>
          </div>
          <div className="w-full">
            <Heading
              text={t('components:briefingSelection:heading')}
              margin={0}
              level="h2"
              align="center"
            />
          </div>
          <div className=" space-y-20 md:space-y-0 w-full md:flex px-4 md:px-0">
            {briefingBranches}
          </div>
          <div className="w-full pt-6 pb-20">
            <a
              className="mr-1 font-sans font-medium text-gray-700 cursor-pointer hover:text-blue-100 hover:underline"
              href={inspirationLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                updateDataLayer({
                  event: 'Content Click',
                  schema: TrackingSchema.content,
                  data: {
                    category: 'Customer Portal',
                    type: 'Customer Portal Redirect',
                    content: 'Formulation Inspiration',
                  },
                });
              }}
            >
              <div className="flex items-center justify-center">
                <span className="block w-5 h-5 mr-2">
                  <Icon name="ArrowRight" />
                </span>
                {t('components:briefingSelection:inspirationLink')}
              </div>
            </a>
          </div>
        </div>
      </div>

      <CountryMarketSelector
        reason="preferences"
        isHidden={!showCountryMarketSelector}
        onCancel={() => {
          setShowCountryMarketSelector(false);
        }}
        onConfirm={() => {
          setShowCountryMarketSelector(false);
        }}
      />
    </PageTracker>
  );
};
